import { animate, group, query, style, transition, trigger } from "@angular/animations";
import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  TemplateRef,
  ViewChild
} from "@angular/core";
import * as _ from 'lodash';
import { AlertsService } from "../alerts/services/alerts.service";

/**
 * Компонент представляет собой кнопку вызова диалогового окна. В окне отображается список опций с чекбоксами, которые можно менять.
 * Чекбоксы могут быть в трех состояниях: `unchecked`, `checked`, `indeterminate`.
 * При нажатии на кнопку `Сохранить` возвращает копию исходного списка с измененными состояниями.
 *
 * Вид кнопки задается с помощью шаблона `<ng-template #button>`. При нажатии на содержимое шаблона происходит вызов метода `showDialog`.
 * Кнопку можно не задавать, вместо этого напрямую вызывать метод `showDialog` из родителя.
 *
 * Можно задать шаблон подсказки в `<ng-template #info>`. Она отобразится на зеленом фоне внизу под списком.
 */
@Component({
  selector: "app-multiple-checkbox-dialog",
  templateUrl: "./multiple-checkbox-dialog.component.html",
  styleUrls: ["./multiple-checkbox-dialog.component.scss"],
  animations: [
    trigger('animation', [
      transition('* => *',
        group([
          query(':enter', [
            style({ opacity: 0, height: 0, padding: 0, overflow: 'hidden' }),
            animate('300ms'),
          ], { optional: true }),
          query(':leave', [
            style({ overflow: 'hidden' }),
            animate('300ms', style({ opacity: 0, height: 0, padding: 0 }))
          ], { optional: true })
        ])
      )
    ])
  ]
})
export class MultipleCheckboxDialogComponent implements OnChanges {
  /**
   * Список опций. Можно задать через шаблон, либо указать в качестве аргумента, если явно вызывать `showDialog(options)` из родителя.
   */
  @Input() options: MultipleCheckboxDialogItem[] = [];

  /**
   * Настройки диалогового окна.
   */
  @Input() settings: MultipleCheckboxDialogSettings = {};

  /**
   * Шаблон кнопки, которая открывает диалог.
   */
  @ContentChild('button') buttonTemplate?: TemplateRef<any>;

  /**
   * Шаблон подсказки. Отображается внизу списка на зеленом фоне.
   */
  @ContentChild('info') infoTemplate?: TemplateRef<any>;

  /**
   * Событие возникает, когда пользователь нажимает `Сохранить`. Возвращает новый список.
   */
  @Output() optionsChange = new EventEmitter<MultipleCheckboxDialogItem[]>();

  @ViewChild("tmplCheckboxList") private tmplCheckboxList: TemplateRef<any>;

  private clonedOptions: MultipleCheckboxDialogItem[]

  protected filteredOptions: MultipleCheckboxDialogItem[];
  protected filter: string;
  protected isDisableAnimation: boolean;

  constructor(private alertsService: AlertsService) { }

  ngOnChanges() {
    this.init();
  }

  private init() {
    this.settings ??= {};
    this.settings.isShowFilter ??= true;

    this.clonedOptions = _.cloneDeep(this.options);
    // Инициализируем неправильно заполненные или пустые статусы значением 'unchecked'.
    this.clonedOptions.forEach(co => {
      if (!['checked', 'unchecked', 'indeterminate'].includes(co.state)) co.state = 'unchecked'
    });
    this.filter = '';
    this.filteredOptions = [...this.clonedOptions];
    this.isDisableAnimation = true; // При открытии диалога анимация списка не нужна.
  }

  /**
   * Отображает диалоговое окно со списком опций.
   * При нажатии на кнопку `Сохранить` вызывает событие `optionsChange` и возвращает измененный список.
   * При отмене возвращает null.
   * @param options Если параметр задан, то список опций берется из него. Иначе из параметра `options`, указанного в шаблоне.
   */
  showDialog(options?: MultipleCheckboxDialogItem[]): Promise<MultipleCheckboxDialogItem[] | null> {
    if (options) this.options = options;

    this.init();

    return new Promise(resolve => {
      this.alertsService.showConfirm({
        header: this.settings.dialogHeader,
        template: { ref: this.tmplCheckboxList, context: {} },
        buttons: [
          {
            title: "Сохранить",
            callback: () => {
              this.optionsChange.emit(this.clonedOptions);
              resolve(this.clonedOptions);
            },
          },
          {
            title: "Отменить",
            callback: () => resolve(null)
          },
        ],
        callbackEsc: () => resolve(null)
      })
    });
  }

  protected changeOption(option: MultipleCheckboxDialogItem) {
    switch (option.state) {
      case 'unchecked':
        const hasIndeterminateState = this.options.find(o => o.id == option.id).state === 'indeterminate';
        if (hasIndeterminateState) option.state = 'indeterminate';
        else option.state = 'checked';
        break;
      case 'indeterminate':
        option.state = 'checked'
        break;
      case 'checked':
      default:
        option.state = 'unchecked';
        break;
    }
  }

  protected changeFilteredOptions() {
    this.isDisableAnimation = false;
    this.filteredOptions = this.clonedOptions.filter(option => option.name.toLowerCase().includes(this.filter.toLowerCase()));

    // Возвращаем в исходное состояние те опции, которые исчезли из списка после фильтрации.
    this.clonedOptions.forEach(co => {
      if (!this.filteredOptions.some(fo => fo.id === co.id)) {
        const initialState = this.options.find(o => o.id === co.id).state;
        co.state = initialState;
      }
    })
  }

  protected clearFilter() {
    this.filter = '';
    this.filteredOptions = [...this.clonedOptions];
  }
}

export interface MultipleCheckboxDialogItem {
  id: string,
  name: string,
  state?: 'checked' | 'unchecked' | 'indeterminate'
}

export interface MultipleCheckboxDialogSettings {
  /**
   * Заголовок диалогового окна.
   */
  dialogHeader?: string;
  /**
   * Показывать ли фильтр. По умолчанию `true`.
   */
  isShowFilter?: boolean;
}
